import {
  DOWN_ARROW,
  END,
  ENTER,
  ESCAPE,
  HOME,
  UP_ARROW,
  hasModifierKey,
} from '@angular/cdk/keycodes';
import {
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';

import {
  APP_FORM_FIELD_DEFAULT_OPTIONS,
  AppFormFieldDefaultOptions,
  FormFieldLabelPosition,
} from 'src/app/components/form-field/form-field.types';

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
  host: {
    '[class.label-above]': 'isLabelAbove',
  },
})
export class AutocompleteComponent implements OnDestroy {
  @Input() placeholder = '';
  @Input() label = '';
  /**
   * Same default-options token as `<app-form-field>`, so a screen that stacks its
   * labels gets both implementations in step - this control renders its own box.
   */
  @Input()
  get labelPosition(): FormFieldLabelPosition {
    return this._labelPosition;
  }
  set labelPosition(value: FormFieldLabelPosition) {
    this._labelPosition = value || this._defaultLabelPosition;
  }
  private _labelPosition!: FormFieldLabelPosition;
  private readonly _defaultLabelPosition: FormFieldLabelPosition;
  /**
   * The asterisk as its own element, so it can be coloured, plus `aria-required`
   * on the input. Presentational **only because this component binds no form
   * control**: every call site is an `autocomplete-form-*` wrapper, which is the
   * `ControlValueAccessor` itself and passes the value down as a plain input, so
   * Angular's `RequiredValidator` has nothing here to match. Do not copy the
   * name onto a control that does bind one -- `app-input` and `app-select` read
   * `hasValidator(Validators.required)` instead, and CLAUDE.md rule 27 says why.
   */
  @Input() required = false;
  @Input() isMultiple = false;
  @Input() keepPanelOpenOnSelect = false;
  @Input() isLoading = false;
  @Input() emptyMessage = 'Nenhuma opção encontrada';
  @Input() isDisabled = false;
  @Input() helperText: string | null = null;
  @Input() isError = false;
  @Input() canRemove = false;
  @Input() skipFilter = false;
  @Input() filterMode: 'local' | 'remote' = 'local';
  @Input() openOnFocus = false;
  @Input() hasDescription = false;
  @Input() optionsLimit = 20;
  @Input() skipFirstFilter = false;

  // 'embedded' hides the internal label/input/pills/helper chrome and
  // expects a #triggerTemplate plus an anchor element via [connectedTo].
  // Defaults to 'standalone' so the 16 existing wrappers stay unchanged.
  @Input() mode: 'standalone' | 'embedded' = 'standalone';
  @Input() connectedTo?: ElementRef | HTMLElement;
  // Narrower than `connectedTo`: only this element (the projected trigger,
  // e.g. <app-input>) is excluded from outside-click close detection.
  // Defaults to `connectedTo`.
  @Input() triggerElement?: ElementRef | HTMLElement;
  @Input() panelWidth?: number | string;
  @Input() displayWith?: (option: AutocompleteOption) => string;
  @Input() autoActiveFirstOption = false;

  @ContentChild('optionTemplate', { read: TemplateRef })
  optionTemplate!: TemplateRef<any>;
  @ContentChild('addOnRightTemplate', { read: TemplateRef })
  addOnRightTemplate!: TemplateRef<any>;
  @ContentChild('emptyOptionsTemplate', { read: TemplateRef })
  emptyOptionsTemplate!: TemplateRef<any>;

  @ContentChild('triggerTemplate', { read: TemplateRef })
  triggerTemplate?: TemplateRef<unknown>;

  @Output() optionSelected = new EventEmitter<AutocompleteOption | null>();
  @Output() optionsSelected = new EventEmitter<AutocompleteOption[]>();
  @Output() inputChange = new EventEmitter<string>();
  @Output() inputBlur = new EventEmitter<void>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() activeIndexChange = new EventEmitter<number | null>();

  @ViewChild('labelRef') labelRef!: ElementRef<HTMLLabelElement>;
  @ViewChild('templateRef') templateRef!: TemplateRef<any>;
  @ViewChildren('optionRef') optionRefs!: QueryList<ElementRef<HTMLElement>>;

  overlayRef!: OverlayRef;
  filteredOptions: AutocompleteOption[] = [];
  selectedOptions: AutocompleteOption[] = [];
  inputValue = '';
  hoveredOption: AutocompleteOption | null = null;
  activeIndex: number | null = null;

  private static _idCounter = 0;
  // Stable base for listbox + option IDs. Consumers may bind this to align
  // with an external trigger's a11y wiring; falsy bindings fall back to the
  // default so the IDs never degenerate to '-listbox' / '-option-N'.
  @Input() set panelId(value: string | null | undefined) {
    if (value) this._panelId = value;
  }
  get panelId(): string {
    return this._panelId;
  }
  private _panelId = `app-autocomplete-${++AutocompleteComponent._idCounter}`;

  private _options: AutocompleteOption[] = [];

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private vcr: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    @Optional()
    @Inject(APP_FORM_FIELD_DEFAULT_OPTIONS)
    defaults?: AppFormFieldDefaultOptions | null
  ) {
    this._defaultLabelPosition = defaults?.labelPosition || 'float';
    this._labelPosition = this._defaultLabelPosition;
  }

  get isLabelAbove(): boolean {
    return this.labelPosition === 'above';
  }

  get activeOptionId(): string | null {
    return this.activeIndex !== null
      ? `${this.panelId}-option-${this.activeIndex}`
      : null;
  }

  get hasValue() {
    const value = this.isMultiple ? this.selectedOptions : this.inputValue;

    return value && value.length > 0;
  }

  get name() {
    return (
      this.label.toLowerCase().split(' ').join('-') +
      this.placeholder.toLowerCase().split(' ').join('-')
    );
  }

  /**
   * Stacked above, the label is a sibling of the `<label>` that wraps the input,
   * so it can only name it by reference. Keyed on `panelId` because `name` is
   * derived from the label text and is neither unique nor always present.
   */
  get staticLabelId(): string | null {
    // The three conditions of the `*ngIf` on that span, and no fewer: naming an
    // element the template did not render points `aria-labelledby` at nothing,
    // which leaves the input with no accessible name at all. Embedded mode has
    // no such span, and today no embedded consumer passes a label -- that is a
    // fact about the one call site, not a property of this component.
    return this.mode === 'standalone' && this.isLabelAbove && !!this.label
      ? `${this.panelId}-label`
      : null;
  }

  get showAddOn() {
    return (!!this.addOnRightTemplate || this.canRemove) && !this.isDisabled;
  }

  get showAddOnIcon() {
    return !this.hasValue || !this.canRemove;
  }

  get showDescriptionBox() {
    return this.hasDescription && this.filteredOptions.length > 0;
  }

  get options() {
    return this._options;
  }

  @Input() set options(options: AutocompleteOption[]) {
    this._options = options;
    this.filteredOptions = options.slice(0, this.optionsLimit);
    // Async (server-driven) results must refresh the already-open panel.
    this.cdr.markForCheck();

    if (this.skipFirstFilter) return;
    this.filterOptions();
    if (this.isRemote()) this._reconcileActive();
  }

  @Input() set initialValue(
    val: AutocompleteOption | AutocompleteOption[] | null
  ) {
    if (this.isMultiple) {
      if (!Array.isArray(val)) {
        console.error('Valide inputs do componente');
        return;
      }

      this.selectedOptions = val;
      return;
    }

    if (Array.isArray(val)) {
      console.error('Valide inputs do componente');
      return;
    }

    this.inputValue = val?.name ?? '';
  }

  ngOnDestroy() {
    this.close();
  }

  handleChange(e: Event) {
    this.inputValue = (e.target as HTMLInputElement).value;

    this.open();
    this.filterOptions();
    this.inputChange.emit(this.inputValue);
  }

  get isPanelOpen(): boolean {
    return !!this.overlayRef && this.overlayRef.hasAttached();
  }

  open() {
    if (this.isDisabled || this.isPanelOpen) return;
    this._openOverlay(this._buildOverlayConfig());
    // Force CD so the embedded view's *ngFor renders and populates optionRefs
    // before the synchronous _setActive → _scrollActiveIntoView path runs.
    this.cdr.detectChanges();
    if (this.autoActiveFirstOption && this.filteredOptions.length) {
      this._setActive(this._findActivable(0, 1));
    }
    this.opened.emit();
  }

  close() {
    const wasOpen = this.isPanelOpen;
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined!;
    }
    if (this.activeIndex !== null) {
      this.activeIndex = null;
      this.activeIndexChange.emit(null);
    }
    if (wasOpen) this.closed.emit();
  }

  handleKeydown(event: KeyboardEvent) {
    if (this.isDisabled) return;
    const isEsc = this._matches(event, ESCAPE, 'Escape');
    const isDown = this._matches(event, DOWN_ARROW, 'ArrowDown');
    const isUp = this._matches(event, UP_ARROW, 'ArrowUp');

    if (isEsc && this.isPanelOpen && !hasModifierKey(event)) {
      event.preventDefault();
      this.close();
      return;
    }
    if (!this.isPanelOpen) {
      if ((isDown || isUp) && !hasModifierKey(event)) {
        event.preventDefault();
        this.open();
      }
      return;
    }
    if (isDown) {
      event.preventDefault();
      this._moveActive(1);
      return;
    }
    if (isUp) {
      event.preventDefault();
      this._moveActive(-1);
      return;
    }
    if (this._matches(event, HOME, 'Home')) {
      event.preventDefault();
      this._setActive(this._findActivable(0, 1));
      return;
    }
    if (this._matches(event, END, 'End')) {
      event.preventDefault();
      this._setActive(this._findActivable(this.filteredOptions.length - 1, -1));
      return;
    }
    if (
      this._matches(event, ENTER, 'Enter') &&
      this.activeIndex !== null &&
      !hasModifierKey(event)
    ) {
      const opt = this.filteredOptions[this.activeIndex];
      if (opt && !opt.noClick) {
        event.preventDefault();
        this.handleOptionClick(opt);
      }
    }
  }

  private _matches(event: KeyboardEvent, code: number, key: string): boolean {
    return event.keyCode === code || event.key === key;
  }

  private _moveActive(delta: number) {
    if (!this.filteredOptions.length) return;
    const start =
      this.activeIndex ?? (delta > 0 ? -1 : this.filteredOptions.length);
    const next = this._findActivable(start + delta, delta);
    if (next !== null) this._setActive(next);
  }

  private _findActivable(fromIdx: number, dir: number): number | null {
    const len = this.filteredOptions.length;
    if (!len) return null;
    for (let i = 0; i < len; i++) {
      const idx = (((fromIdx + i * dir) % len) + len) % len;
      if (!this.filteredOptions[idx].noClick) return idx;
    }
    return null;
  }

  private _setActive(idx: number | null) {
    if (this.activeIndex === idx) return;
    this.activeIndex = idx;
    this.activeIndexChange.emit(idx);
    this.cdr.markForCheck();
    this._scrollActiveIntoView();
  }

  private _scrollActiveIntoView() {
    if (!this.overlayRef || this.activeIndex === null) return;
    const target = this.optionRefs?.get(this.activeIndex)?.nativeElement;
    const panel = this.overlayRef.overlayElement.querySelector(
      '.options'
    ) as HTMLElement | null;
    if (!target || !panel) return;
    const top = target.offsetTop;
    const bottom = top + target.offsetHeight;
    if (top < panel.scrollTop) panel.scrollTop = top;
    else if (bottom > panel.scrollTop + panel.clientHeight)
      panel.scrollTop = bottom - panel.clientHeight;
  }

  private _overlayAnchor(): ElementRef | HTMLElement {
    return this.connectedTo ?? this.labelRef;
  }

  private _buildOverlayConfig() {
    const anchor = this._overlayAnchor();
    const anchorEl =
      anchor instanceof ElementRef ? anchor.nativeElement : anchor;
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(anchor)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);
    const embedded = this.mode === 'embedded';
    return {
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: !embedded,
      backdropClass: 'cdk-overlay-backdrop',
      width: this.panelWidth ?? anchorEl.offsetWidth,
      maxHeight: embedded ? '256px' : '210px',
      panelClass: embedded ? 'app-autocomplete__panel--embedded' : undefined,
    };
  }

  private _openOverlay(
    config: ReturnType<AutocompleteComponent['_buildOverlayConfig']>
  ) {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.overlayRef = this.overlay.create(config);
    if (this.mode === 'embedded') {
      // Exclude only the trigger element (not the whole anchor) so clicks
      // on sibling form-field chrome (e.g. matSuffix clear button) close
      // the panel. NgZone.run ensures the close path triggers CD.
      const trigger = this.triggerElement ?? this._overlayAnchor();
      const triggerEl =
        trigger instanceof ElementRef ? trigger.nativeElement : trigger;
      this.overlayRef.outsidePointerEvents().subscribe((event) => {
        const target = event.target as Node | null;
        if (target && triggerEl.contains(target)) return;
        this.ngZone.run(() => this.close());
      });
    } else {
      this.overlayRef.backdropClick().subscribe(() => this.close());
    }
    const portal = new TemplatePortal(this.templateRef, this.vcr);
    this.overlayRef.attach(portal);
    this.cdr.markForCheck();
  }

  private isRemote(): boolean {
    return this.skipFilter || this.filterMode === 'remote';
  }

  filterOptions() {
    if (this.isRemote()) return;

    const needle = this.inputValue.toLowerCase();

    this.filteredOptions = this.options.filter((option) => {
      if (!!option?.searchTerms) {
        return option.searchTerms.some((searchTerm) =>
          searchTerm.toLowerCase().includes(needle)
        );
      }

      return option.name.toLowerCase().includes(needle);
    });
    this._reconcileActive();
  }

  // Shared by `filterOptions` and `set options` (when skipFilter=true) so
  // async dataSource swaps never leave activeIndex pointing past the new
  // list. Computes the final activeIndex first and emits only the net
  // transition (no intermediate null → 0 noise on stale-reset + autoActive).
  private _reconcileActive() {
    const len = this.filteredOptions.length;
    const stale = this.activeIndex !== null && this.activeIndex >= len;
    const shouldReset = !this.isRemote() || stale;
    const after = shouldReset ? null : this.activeIndex;
    const next =
      this.autoActiveFirstOption && len && after === null
        ? this._findActivable(0, 1)
        : after;
    if (next === this.activeIndex) return;
    if (next === null) {
      this.activeIndex = null;
      this.activeIndexChange.emit(null);
    } else {
      this._setActive(next);
    }
  }

  handleOptionClick(option: AutocompleteOption) {
    if (option.noClick) return;

    if (this.isMultiple) {
      const alreadySelected = this.isOptionSelected(option);
      const withoutOption = this.selectedOptions.filter(
        (selected) => selected.value !== option.value
      );

      this.selectedOptions =
        this.keepPanelOpenOnSelect && alreadySelected
          ? withoutOption
          : [...withoutOption, option];
      this.inputValue = '';
      this.optionsSelected.emit(this.selectedOptions);

      if (this.keepPanelOpenOnSelect) {
        this.filterOptions();
        return;
      }
    } else {
      this.inputValue = this.displayWith
        ? this.displayWith(option)
        : option.name;
      this.optionSelected.emit(option);
    }
    this.close();
  }

  handleRemove(option: AutocompleteOption) {
    if (this.isDisabled) return;

    this.selectedOptions = this.selectedOptions.filter(
      (selected) => selected.value !== option.value
    );
    this.optionsSelected.emit(this.selectedOptions);
  }

  isOptionSelected(option: AutocompleteOption): boolean {
    return this.selectedOptions.some(
      (selected) => selected.value === option.value
    );
  }

  showAsSelected(option: AutocompleteOption): boolean {
    return this.keepPanelOpenOnSelect && this.isOptionSelected(option);
  }

  removeButtonClick() {
    this.inputValue = '';

    if (this.isMultiple) {
      this.selectedOptions = [];
      this.optionsSelected.emit([]);
    } else {
      this.optionSelected.emit(null);
    }
  }

  clearInput() {
    this.inputValue = '';
  }

  handleInputBlur() {
    this.inputBlur.emit();
    if (this.keepPanelOpenOnSelect) this.close();
  }

  handleInputFocus() {
    if (this.openOnFocus) this.open();
  }

  setHoveredOption(option: AutocompleteOption) {
    this.hoveredOption = option;
  }
}

export interface AutocompleteOptionStatus {
  active: boolean;
  tooltip?: string;
}

export interface AutocompleteOption {
  name: string;
  value: string;
  description?: string;
  noClick?: boolean;
  selectedName?: string;
  searchTerms?: string[];
  data?: Record<string, any>;
  status?: AutocompleteOptionStatus;
}
